import React, { useState, useEffect } from 'react';
import { EmptyState, F } from './shared';

const GRADIENT = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRADIENT_TEXT = {
  background: GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const highlight = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '<span style="color:#7dd3fc">$1</span>')
    .replace(/\b(You are|you are|Build|Design|Create|Include|Raise|Add|Use|Make)\b/g,
      m => `<span style="color:#7dd3fc">${m}</span>`)
    .replace(/'([^']+)'/g,
      m => `<span style="color:#86efac">${m}</span>`)
    .replace(/\$[\d,.]+[BMKbmk+%]*/g,
      m => `<span style="color:#fbbf24">${m}</span>`)
    .replace(/\b(\d+[\d,.]*[BMKbmk%x+]*)\b/g,
      m => `<span style="color:#fbbf24">${m}</span>`);
};

const CustomPrompts = ({ customPrompts }) => {
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCopy = (text, idx) => {
    // Create a temporary textarea for reliable copying
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, 99999);
    
    try {
      document.execCommand('copy');
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch (err) {
      // Fallback to clipboard API
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(null), 2000);
      }
    }
    
    document.body.removeChild(textarea);
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>
      <h2 style={{
        fontFamily: "'Sansation', sans-serif",
        fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
        fontWeight: 800,
        letterSpacing: '-0.03em',
        color: '#0f172a', 
        margin: isMobile ? '0 0 14px 0' : '0 0 20px 0',
      }}>
        Custom Deck Building Prompts
      </h2>

      {customPrompts.length > 0 ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: isMobile ? 12 : 14 
        }}>
          {customPrompts.map((prompt, i) => {
            // index 0 = blue (gradient), 1 = green, 2 = pink — cycles for others
            const dotColors = [null, '#1b8c2e', '#B5325B'];
            const dotColor = dotColors[i % dotColors.length];
            const isBlue = dotColor === null;

            return (
              <div key={i} style={{
                background: '#f8fafc', 
                borderRadius: isMobile ? 14 : (isTablet ? 16 : 20),
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}>

                {/* ── Card header row ── */}
                <div style={{
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: isMobile ? '10px 14px' : (isTablet ? '11px 16px' : '12px 18px'),
                  borderBottom: '0.5px solid rgba(0,0,0,0.06)',
                  gap: isMobile ? 8 : 0,
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobile ? 7 : 8,
                    minWidth: 0,
                    flex: 1,
                  }}>
                    {/* Dot — full app gradient for blue slot, solid colour for others */}
                    <div style={{
                      width: isMobile ? 7 : 8, 
                      height: isMobile ? 7 : 8, 
                      borderRadius: '50%', 
                      flexShrink: 0,
                      background: isBlue ? GRADIENT : dotColor,
                    }} />
                    <span style={{
                      fontFamily: F.display,
                      fontSize: isMobile ? 12.5 : 13.5, 
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      color: '#0f172a',
                      wordBreak: 'break-word',
                    }}>
                      {prompt.label || `Prompt ${i + 1}`}
                    </span>
                  </div>

                  <button
                    onClick={() => handleCopy(prompt.text, i)}
                    style={{
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: isMobile ? 5 : 6,
                      padding: isMobile ? '5px 10px' : '5px 12px', 
                      borderRadius: isMobile ? 6 : 8,
                      border: '0.5px solid rgba(0,0,0,0.1)',
                      background: 'transparent',
                      fontFamily: F.body,
                      fontSize: isMobile ? 10 : 11, 
                      fontWeight: 500,
                      color: copiedIdx === i ? '#16a34a' : '#6e6e73',
                      cursor: 'pointer',
                      transition: 'color 0.15s',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {copiedIdx === i ? (
                      <>
                        <span style={{ fontSize: isMobile ? 11 : 12 }}>✓</span> 
                        {!isMobile && 'Copied!'}
                        {isMobile && <span style={{ fontSize: 10 }}>✓</span>}
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: isMobile ? 11 : 12 }}>📋</span> 
                        {isMobile ? '' : 'Copy'}
                      </>
                    )}
                  </button>
                </div>

                {/* ── Code block — updated to match app gradient ── */}
                <div style={{
                  background: GRADIENT,
                  padding: isMobile ? '14px 16px' : (isTablet ? '16px 18px' : '18px 20px'),
                  maxHeight: isMobile ? 180 : (isTablet ? 200 : 220), 
                  overflowY: 'auto',
                }}>
                  <pre
                    style={{
                      fontFamily: F.mono,
                      fontSize: isMobile ? 10 : 11, 
                      lineHeight: 1.7,
                      color: 'rgba(255,255,255,0.92)',
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-word',
                      margin: 0,
                    }}
                    dangerouslySetInnerHTML={{ __html: highlight(prompt.text) }}
                  />
                </div>

              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          background: '#ffffff', 
          borderRadius: isMobile ? 14 : 20,
          padding: isMobile ? 20 : 28,
          border: '0.5px solid rgba(0,0,0,0.06)',
        }}>
          <EmptyState
            icon="Terminal"
            title="No custom prompts"
            desc="AI deck rebuilding prompts will populate from your HTML report."
          />
        </div>
      )}
    </div>
  );
};

export default CustomPrompts;